import React, { ChangeEvent, KeyboardEvent, useContext, useEffect, useMemo, useState } from 'react';
import { BuildingOfficeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, Cog6ToothIcon, PlusIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';
import { useOutsideClick } from '@app/component/outside-click/HandleOutsideClickHook';
import { CompanyHotelsContext } from '@app/site/secured/rateshopper/rates/provider/CompanyHotelsProvider';
import { Link, useNavigate } from 'react-router-dom';
import useSiteState from '@app/state/SiteState';
import CompanyHotelLite from '@app/model/company_hotel/CompanyHotelLite';
import { useDomainTransitionContext } from '@app/context/DomainTransitionContext';
import { useQuery } from '@tanstack/react-query';
import CompanyService from '@app/model/company/service/CompanyService';
import { CompanyQueryKeys } from '@app/model/company/service/CompanyGraphQLQueryKeys';
import CompanySettings from '@app/model/company/CompanySettings';

const CompanyHotelButtonSelectorFragment: React.FunctionComponent<{
	hideCompanyHotelSelector?: boolean,
	lockCompanyHotelSelector?: boolean
}> = ({ hideCompanyHotelSelector, lockCompanyHotelSelector }) => {

	const { data, isLoading, isSuccess } = useQuery({
		queryKey: CompanyQueryKeys.settings(),
		queryFn: () => CompanyService.findCompanySettings(),
		refetchOnWindowFocus: false
	});

	if (isLoading) return <div />;
	if (!isSuccess) return <div />;

	return <CompanyHotelButtonSelector hideCompanyHotelSelector={hideCompanyHotelSelector}
																		 lockCompanyHotelSelector={lockCompanyHotelSelector} companySettings={data} />;
};

const CompanyHotelButtonSelector: React.FunctionComponent<{
	hideCompanyHotelSelector?: boolean,
	lockCompanyHotelSelector?: boolean,
	companySettings: CompanySettings
}> = ({ hideCompanyHotelSelector, lockCompanyHotelSelector, companySettings }) => {

	const [isActive, setActive] = useState<boolean>(false);
	const ref = useOutsideClick(() => setActive(false));
	const siteState = useSiteState();
	const { companyHotels: allCompanyHotels, selectedCompanyHotel } = useContext(CompanyHotelsContext);

	const [nameFilter, setNameFilter] = useState<string>('');
	const [activeCompanyHotelIndex, setActiveCompanyHotelIndex] = useState<number>(-1);
	const navigate = useNavigate();
	const { startTransition } = useDomainTransitionContext();

	const companyHotels = useMemo<CompanyHotelLite[]>(() => {
		if (nameFilter.length === 0) return allCompanyHotels;

		return allCompanyHotels.filter(companyHotel => companyHotel.mainHotelName.toLowerCase().includes(nameFilter.toLowerCase()));
	}, [allCompanyHotels, nameFilter]);

	useEffect(() => {
		if (!isActive) return;
		if (companyHotels.length === 0) {
			setActiveCompanyHotelIndex(-1);
			return;
		}

		const selectedCompanyHotelIndex = companyHotels.findIndex(companyHotel => isCompanyHotelSelected(companyHotel.id));
		setActiveCompanyHotelIndex(selectedCompanyHotelIndex >= 0 ? selectedCompanyHotelIndex : 0);
	}, [companyHotels, isActive, selectedCompanyHotel]);

	const isCompanyHotelSelected = (companyHotelId: number) => {
		return !!selectedCompanyHotel && selectedCompanyHotel.id === companyHotelId;
	};

	const selectCompanyHotel = (companyHotelId: number) => {
		startTransition();
		siteState.setSelectedCompanyHotelId(companyHotelId);
		setActive(false);
		navigate('/secured/dashboard');
	};

	const filterValues = (event: ChangeEvent<HTMLInputElement>) => {
		setNameFilter(event.target.value);
	};

	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (companyHotels.length === 0) return;

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setActiveCompanyHotelIndex(currentIndex => Math.min(currentIndex + 1, companyHotels.length - 1));
		}

		if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActiveCompanyHotelIndex(currentIndex => Math.max(currentIndex - 1, 0));
		}

		if (event.key === 'Enter' && activeCompanyHotelIndex >= 0) {
			event.preventDefault();
			selectCompanyHotel(companyHotels[activeCompanyHotelIndex].id);
		}
	};

	if (hideCompanyHotelSelector) return <></>;

	return !lockCompanyHotelSelector ? <div className="relative  px-7" ref={ref}>
		<button type="button" onClick={() => setActive(!isActive)}
						className="inline-flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 disabled:pointer-events-none disabled:opacity-50"
						aria-expanded="false">
			<span
				className="truncate max-w-[40vw] md:max-w-[55vw]">{selectedCompanyHotel!.mainHotel.competitors[0].hotel.name}</span>
			<ChevronDownIcon className="h-5 w-5" />
		</button>
		{isActive && <div className="absolute left-1/2 z-10 mt-5 flex w-screen max-w-max -translate-x-1/2 px-4">
			<div
				className="w-screen max-w-md flex-auto overflow-hidden rounded-3xl bg-white text-sm leading-6 shadow-lg ring-1 ring-gray-900/5">
				{allCompanyHotels.length > 9 && <div className="pt-5 px-6 mb-2">
					<div className="grid w-full max-w-lg grid-cols-1">
						<input
							type="search"
							name="search"
							aria-label="Search"
							autoFocus
							autoComplete="off"
							className="col-start-1 row-start-1 block input w-full rounded-md bg-white py-1.5 pl-10 pr-3 sm:text-sm/6"
							placeholder="Search your properties"
							onChange={filterValues}
							onKeyDown={onKeyDown}
							value={nameFilter} />
						<MagnifyingGlassIcon
							className="pointer-events-none col-start-1 row-start-1 ml-3 h-5 w-5 self-center text-gray-400" />
					</div>
				</div>}
				<div className="py-4 p-1 overflow-y-auto max-h-96">
					{companyHotels.map((item, idx) => {
						const isActiveCompanyHotel = activeCompanyHotelIndex === idx;
						const isSelectedCompanyHotel = isCompanyHotelSelected(item.id);

						return <div
							key={item.mainHotelId}
							onMouseEnter={() => setActiveCompanyHotelIndex(idx)}
							className={Utilities.classNames('group relative flex gap-x-6 rounded-lg p-4 transition-colors', isActiveCompanyHotel ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : isSelectedCompanyHotel ? 'bg-gray-50' : 'hover:bg-blue-50/70')}>
						<div
							className={Utilities.classNames('mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg', isActiveCompanyHotel ? 'bg-white shadow-sm' : 'bg-gray-50 group-hover:bg-white')}>
							<BuildingOfficeIcon
								className={Utilities.classNames('h-6 w-6', isSelectedCompanyHotel || isActiveCompanyHotel ? 'text-blue-600' : 'group-hover:text-blue-600')}
								aria-hidden="true" />
						</div>
						<div>
							<a onClick={() => selectCompanyHotel(item.id)}
								className={Utilities.classNames('font-semibold cursor-pointer', isSelectedCompanyHotel || isActiveCompanyHotel ? 'text-blue-600' : ' text-gray-900 group-hover:text-blue-600')}>
								{item.mainHotelName}
								<span className="absolute inset-0"></span>
							</a>
							<p
								className="mt-1 text-gray-600 leading-tight">{item.mainHotelLocation?.address}, {item.mainHotelLocation?.city} {item.mainHotelLocation?.country?.name}</p>
						</div>
					</div>;
					})}
				</div>
				{!!selectedCompanyHotel && selectedCompanyHotel.hasRatesModule() && <>
					{companySettings.enableAddCompanyHotel &&
						<div className="grid grid-cols-2 divide-x divide-gray-900/5 bg-gray-50">
							<Link to="/secured/rateshopper/rates/add-new-property"
										className="flex items-center justify-center gap-x-2.5 p-3 text-sm font-semibold text-gray-900 hover:bg-gray-100 cursor-pointer">
								<PlusIcon
									className="h-4 w-4 flex-none text-gray-400"
									aria-hidden="true" />
								Add new property
							</Link>
							<Link to={'/secured/rateshopper/rates/settings/competitors'}
										className="flex items-center justify-center gap-x-2.5 p-3 text-sm font-semibold text-gray-900 hover:bg-gray-100 cursor-pointer">
								<Cog6ToothIcon
									className="h-4 w-4 flex-none text-gray-400"
									aria-hidden="true" />
								Property Settings
							</Link>
						</div>}
				{!companySettings.enableAddCompanyHotel && <Link to={'/secured/rateshopper/rates/settings/competitors'}
																												 className="flex items-center justify-center gap-x-2.5 p-3 text-sm font-semibold text-gray-900 hover:bg-gray-100 cursor-pointer">
					<Cog6ToothIcon
						className="h-4 w-4 flex-none text-gray-400"
						aria-hidden="true" />
					Property Settings
				</Link>}
				</>}
			</div>
		</div>}
	</div> : <div
		className="inline-flex items-center pl-7 text-sm font-semibold leading-6 text-gray-900 disabled:pointer-events-none disabled:opacity-50">
		<span
			className="truncate max-w-[40vw] md:max-w-[55vw]">{selectedCompanyHotel!.mainHotel.competitors[0].hotel.name}</span>
	</div>;
};

export default CompanyHotelButtonSelectorFragment;
